/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useState } from 'react';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { useRouter } from 'next/router';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import usePrevious from '@/utils/usePrevious';
import { SearchResultsDefault } from '../components/SearchComponent/SearchResultsDefault';
import { useAutoCloseOnOrderReady } from '@/features/sisterStoreFlyout';
import Head from 'next/head';
import { useSearchProductData } from '../api/getSearchProductData';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import CMSSearchResultsPage from '../components/CMSSearchResultsPage';
import { parseUrl, sanitizeUrlWithParamList } from '@/utils/urlHelpers';
import { acceptableShelfQueryParams } from '@/constants/queryParams';
import { View } from '@az/starc-ui';
import ShelfSkeleton from '@/components/Skeletons/ShelfSkeleton';
import TwoColumnSkeleton from '@/components/Skeletons/TwoColumn';
import { useContentStackLivePreview } from '@/hooks/useContentStackLivePreview';

export const SearchPageComponent = () => {
  const router = useRouter();
  const preferredVehicle = usePreferredVehicle();
  const vehicleId = preferredVehicle?.catalogVehicleId;
  const previousVehicleId = usePrevious(preferredVehicle?.catalogVehicleId);
  const { data: storeDetails } = useStoreDetailsData();
  const prevStoreNumber = usePrevious(storeDetails?.storeNumber);
  const previousQuery = usePrevious(router.query);
  const queryDisplayLabel =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';

  const [viewingPartTypes, setViewingPartTypes] = useState(router.query.partTypesView === 'true');
  const {
    data: searchProductData,
    isLoading: searchProductLoading,
    fetchStatus: searchProductFetchStatus,
  } = useSearchProductData({
    enabled: !viewingPartTypes,
  });
  const isIdle = searchProductFetchStatus === 'idle';
  const isLoading = searchProductLoading;
  const [analyticsCalled, setAnalyticsCalled] = useState(false);
  const [analyticsEventType, setAnalyticsEventType] = useState<string | undefined>(
    'successfulSearch'
  );
  useEffect(() => {
    const isDifferentSearchPage =
      !!previousQuery?.searchText &&
      !!queryDisplayLabel &&
      queryDisplayLabel !== previousQuery?.searchText;

    const filterEventType =
      !isDifferentSearchPage &&
      (previousQuery?.facet !== router.query.facet ||
        previousQuery?.maxPrice !== router.query.maxPrice ||
        previousQuery?.minPrice !== router.query.minPrice)
        ? (previousQuery?.facet?.length ?? 0) > (router.query?.facet?.length ?? 0) ||
          (previousQuery?.maxPrice && !router.query.maxPrice) ||
          (previousQuery?.minPrice && !router.query.minPrice)
          ? 'removeFilter'
          : 'applyFilter'
        : undefined;

    if (
      (!isLoading && !isIdle) ||
      isDifferentSearchPage ||
      Boolean(filterEventType) ||
      vehicleId !== previousVehicleId ||
      storeDetails?.storeNumber !== prevStoreNumber ||
      previousQuery?.sort !== router.query?.sort ||
      previousQuery?.recsPerPage !== router.query?.recsPerPage
    ) {
      setAnalyticsCalled(false);
      if (filterEventType) {
        setAnalyticsEventType(filterEventType);
      } else if (
        isDifferentSearchPage ||
        !previousQuery?.searchText ||
        previousQuery?.searchText === queryDisplayLabel
      ) {
        setAnalyticsEventType('successfulSearch');
      } else {
        setAnalyticsEventType(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    queryDisplayLabel,
    previousQuery,
    router.query,
    isIdle,
    vehicleId,
    previousVehicleId,
    storeDetails,
    prevStoreNumber,
  ]);

  useAutoCloseOnOrderReady();

  const skuIds = searchProductData?.records
    ?.slice(0, 10)
    .map((record) => record.skuNumber)
    .join(';');
  const certonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType: certonaPageType.noSearchResultPage,
      vehicleID: vehicleId,
      skuIds,
    },
    [vehicleId, skuIds, searchProductFetchStatus],
    (searchProductData?.total === 0 || !!skuIds) ?? false
  );
  const { isCSProductListingPageEnabled } = useContentStackPLPData({ enabled: true });
  useContentStackLivePreview({ enabled: isCSProductListingPageEnabled });

  const searchPageHead = (
    <Head>
      <meta name="robots" content="noindex" />
      {/* Disable automatic telephone number formating to prevent part numbers or search terms from being mistakenly
    converted into phone number links by Safari on iOS, which can cause a React hydration error*/}
      <meta name="format-detection" content="telephone=no" />
    </Head>
  );

  useEffect(() => {
    if (!searchProductData.redirectUrl) {
      return;
    }

    const { url, query: queryParams } = parseUrl(searchProductData?.redirectUrl || '');
    const sanitizedRedirectPath = sanitizeUrlWithParamList(
      {
        ...queryParams,
        ...(router.query.searchText && {
          searchText: router.query.searchText,
        }),
        ...{ redirectUrl: 'true' },
      },
      acceptableShelfQueryParams,
      url
    );

    void router.replace(sanitizedRedirectPath);
  }, [searchProductData.redirectUrl, router]);

  if (searchProductData.redirectUrl) {
    return (
      <>
        {searchPageHead}
        {!viewingPartTypes ? (
          <View padding={[10, 0]}>
            <ShelfSkeleton />
          </View>
        ) : (
          <TwoColumnSkeleton />
        )}
      </>
    );
  }

  return (
    <>
      {searchPageHead}
      {isCSProductListingPageEnabled && (
        <CMSSearchResultsPage
          certonaRecommendations={certonaRecommendations}
          viewingPartTypes={viewingPartTypes}
          setViewingPartTypes={setViewingPartTypes}
          setAnalyticsCalled={setAnalyticsCalled}
          analyticsCalled={analyticsCalled}
          analyticsEventType={analyticsEventType}
        />
      )}
      {!isCSProductListingPageEnabled && (
        <SearchResultsDefault
          analyticsCalled={analyticsCalled}
          analyticsEventType={analyticsEventType}
          setAnalyticsCalled={setAnalyticsCalled}
          certonaRecommendations={certonaRecommendations}
          viewingPartTypes={viewingPartTypes}
          setViewingPartTypes={setViewingPartTypes}
        />
      )}
    </>
  );
};
